import { useState, useMemo } from "react";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";

const PERIODS = [
  { id: "all", label: "Todo" },
  { id: "weekly", label: "Semanal" },
  { id: "biweekly", label: "Quincenal" },
  { id: "monthly", label: "Mensual" },
];

const MONTH_NAMES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function getWeekRange(year, weekOffset) {
  const now = new Date();
  const ref = new Date(year, now.getMonth(), now.getDate());
  const day = ref.getDay() || 7;
  const monday = new Date(ref);
  monday.setDate(ref.getDate() - day + 1 + weekOffset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: fmtDate(monday), end: fmtDate(sunday), label: `${monday.getDate()} ${MONTH_NAMES[monday.getMonth()]} - ${sunday.getDate()} ${MONTH_NAMES[sunday.getMonth()]}` };
}

function getBiweeklyRange(year, month, half) {
  const lastDay = new Date(year, month, 0).getDate();
  if (half === 1) {
    return { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-15`, label: `1-15 ${MONTH_NAMES[month - 1]}` };
  }
  return { start: `${year}-${pad(month)}-16`, end: `${year}-${pad(month)}-${lastDay}`, label: `16-${lastDay} ${MONTH_NAMES[month - 1]}` };
}

function getMonthRange(year, month) {
  const lastDay = new Date(year, month, 0).getDate();
  return { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-${lastDay}`, label: `${MONTH_NAMES[month - 1]} ${year}` };
}

function pad(n) { return String(n).padStart(2, "0"); }
function fmtDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

export function usePeriodFilter(items, year) {
  const [period, setPeriod] = useState("all");
  const [offset, setOffset] = useState(0);
  const now = new Date();
  const currentMonth = now.getMonth() + 1;

  const range = useMemo(() => {
    if (period === "all") return null;
    if (period === "weekly") return getWeekRange(year, offset);
    if (period === "biweekly") {
      const baseHalf = now.getDate() <= 15 ? 0 : 1;
      const baseIndex = (currentMonth - 1) * 2 + baseHalf + offset;
      const clampedIndex = Math.max(0, Math.min(23, baseIndex));
      const m = Math.floor(clampedIndex / 2) + 1;
      const h = (clampedIndex % 2) + 1;
      return getBiweeklyRange(year, m, h);
    }
    if (period === "monthly") {
      let m = currentMonth + offset;
      if (m > 12) m = 12;
      if (m < 1) m = 1;
      return getMonthRange(year, m);
    }
    return null;
  }, [period, offset, year, currentMonth]);

  const filtered = useMemo(() => {
    if (!range) return items;
    return items.filter(item => {
      const d = item.date?.slice(0, 10);
      return d >= range.start && d <= range.end;
    });
  }, [items, range]);

  const changePeriod = (p) => { setPeriod(p); setOffset(0); };

  const PeriodFilter = () => (
    <div className="flex items-center gap-2 flex-wrap" data-testid="period-filter">
      <div className="flex gap-0.5 bg-slate-100 p-0.5 rounded-lg">
        {PERIODS.map(p => (
          <button key={p.id} onClick={() => changePeriod(p.id)}
            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${period === p.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`period-${p.id}`}>
            {p.label}
          </button>
        ))}
      </div>
      {range && (
        <div className="flex items-center gap-1">
          <button onClick={() => setOffset(o => o - 1)} className="p-1 hover:bg-slate-100 rounded" data-testid="period-prev">
            <CaretLeft size={14} className="text-slate-500" />
          </button>
          <span className="text-xs font-medium text-slate-600 bg-white border px-2.5 py-1 rounded-lg min-w-[120px] text-center flex items-center justify-center gap-1.5">
            <CalendarBlank size={12} className="text-slate-400" />
            {range.label}
          </span>
          <button onClick={() => setOffset(o => o + 1)} className="p-1 hover:bg-slate-100 rounded" data-testid="period-next">
            <CaretRight size={14} className="text-slate-500" />
          </button>
        </div>
      )}
    </div>
  );

  return { filtered, PeriodFilter, range };
}
